import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { EmailAuthProvider, GoogleAuthProvider, linkWithPopup, linkWithCredential, fetchSignInMethodsForEmail, sendPasswordResetEmail, getAuth, } from 'firebase/auth';
const AuthMethodsContext = createContext(undefined);
export const AuthMethodsProvider = ({ children }) => {
    const [loaded, setLoaded] = useState(false);
    const [emailPasswordLinked, setEmailPasswordLinked] = useState(false);
    const [googleLinked, setGoogleLinked] = useState(false);
    const [auth, setAuth] = useState(undefined);
    useEffect(() => {
        const auth = getAuth();
        if (auth) {
            setAuth(auth);
        }
    }, []);
    const checkLinkedMethods = useCallback(async () => {
        if (!auth)
            return;
        await auth.authStateReady();
        const currentUser = auth?.currentUser;
        if (!currentUser) {
            setEmailPasswordLinked(false);
            setGoogleLinked(false);
            setLoaded(true);
            return;
        }
        try {
            const methods = currentUser.email
                ? await fetchSignInMethodsForEmail(auth, currentUser.email)
                : [];
            const providerIds = currentUser.providerData?.map((provider) => provider.providerId) ?? [];
            setEmailPasswordLinked(methods.includes('password') || providerIds.includes('password'));
            setGoogleLinked(methods.includes('google.com') || providerIds.includes('google.com'));
        }
        catch (error) {
            console.error('Error checking linked methods:', error);
        }
        setLoaded(true);
    }, [auth]);
    useEffect(() => {
        checkLinkedMethods();
    }, [checkLinkedMethods, auth]);
    const linkEmailPassword = useCallback(async (email, password) => {
        const currentUser = auth?.currentUser;
        if (!currentUser)
            throw new Error('No user logged in');
        try {
            const credential = EmailAuthProvider.credential(email, password);
            await linkWithCredential(currentUser, credential);
            await checkLinkedMethods();
        }
        catch (error) {
            console.error('Error linking email/password:', error);
            throw error;
        }
    }, [checkLinkedMethods]);
    const linkGoogle = useCallback(async () => {
        const currentUser = auth?.currentUser;
        if (!currentUser)
            throw new Error('No user logged in');
        try {
            const provider = new GoogleAuthProvider();
            await linkWithPopup(currentUser, provider);
            await checkLinkedMethods();
        }
        catch (error) {
            console.error('Error linking Google:', error);
            throw error;
        }
    }, [checkLinkedMethods]);
    const sendResetPasswordEmail = useCallback(async () => {
        const currentUser = auth?.currentUser;
        if (!currentUser?.email) {
            return;
        }
        if (!auth)
            return;
        await sendPasswordResetEmail(auth, currentUser.email);
    }, [auth]);
    const contextValue = {
        loaded,
        emailPasswordLinked,
        googleLinked,
        linkEmailPassword,
        linkGoogle,
        sendResetPasswordEmail
    };
    return _jsx(AuthMethodsContext.Provider, { value: contextValue, children: children });
};
export const useAuthMethods = () => {
    const context = useContext(AuthMethodsContext);
    if (context === undefined) {
        throw new Error('useAuthMethods must be used within an AuthMethodsProvider');
    }
    return context;
};
